import React, { useMemo, Suspense } from 'react';
import { log } from 'log';
import { compose, prop } from 'ramda';
import { withProps } from 'recompose';
import { useUserSelector } from 'js/store/hooks';
import { usePrevious } from 'js/hooks/usePrevious';
import { useGameConfig } from 'js/components/common/Game';
import { FavoriteIcon } from 'js/components/common/Game/FavoriteIcon';
import { withTranslations } from 'js/components/hocs/withTranslations';
import GamesViewMini from 'js/components/common/GamesView/GamesViewMini';
import { DemoButton, PlayButton } from 'js/components/common/Game/Buttons';
import { useLiveGameConfig, useGameImageUrls, useLiveGames } from 'js/hooks/games';

import { Jackpot } from 'js/components/main/Games/Jackpot';
import { useAutoSizeText } from './hooks';
import withGameDescription from './withGameDescription';

import style from './index.scss';

export const Title = ({ children, className }) => {
    const { containerRef, textRef } = useAutoSizeText();

    return (
        <div className={className} ref={containerRef}>
            <span ref={textRef}>{children}</span>
        </div>
    );
};

export const GameInfoBare = ({
    t,
    name,
    demoModeSupport,
    showFavorite,
    description,
    code,
    queryString,
    hideButtons,
    gameLaunchType,
    gamesYouMightLike,
    liveLaunchAlias
}) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = liveGameConfig?.launchAlias;
    const { dynamicImageUrl, limits, openingDateTime, isScheduled, seatsInfo } = liveGameConfig || {};
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    // prevImageUrl is needed to avoid image blinking to default while new live image is loading
    const prevImageUrl = usePrevious(imageUrls[0]);
    const gameImages = isLiveGame && prevImageUrl
        ? [imageUrls[0], prevImageUrl, ...imageUrls.slice(1)]
        : imageUrls;
    const gameImageBackGround = gameImages.map(url => `url("${url}")`).join(', ');

    const liveLaunchAliases = useMemo(() => ({
        ...liveLaunchAlias && { liveLaunchAliases: [liveLaunchAlias] }
    }), [liveLaunchAlias]);

    useLiveGames(liveLaunchAliases);

    return (
        <div className={style.container}>
            <div className={style.top}>
                <div className={style.logo}>
                    <div className={style.logoImage} style={{ backgroundImage: gameImageBackGround }} />
                    {isScheduledLiveGame && (
                        <div className={style.scheduledLiveGame}>
                            {openingDateTime
                                ? (
                                    <>
                                        <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                        <span>{openingDateTime}</span>
                                    </>
                                )
                                : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                            }
                        </div>
                    )}
                </div>
                <div className={style.card}>
                    <div className={style.cardInner}>
                        <Suspense fallback="">
                            <Jackpot
                                gameCode={code}
                                className={style.jackpot}
                                showGuaranteedHitTime={true}
                            />
                        </Suspense>
                        {showFavorite && <FavoriteIcon isFeatured={true} className={style.favorite} code={code} />}
                        <Title className={style.gameTitle}>{name}</Title>
                    </div>
                    {!hideButtons && !isScheduledLiveGame && (
                        <div className={style.buttons}>
                            {demoModeSupport && !isLiveGame && (
                                <DemoButton
                                    code={code}
                                    name={name}
                                    queryString={queryString}
                                    gameLaunchType={gameLaunchType}
                                />
                            )}
                            <PlayButton
                                code={code}
                                name={name}
                                queryString={queryString}
                                className={style.real}
                                gameLaunchType={gameLaunchType}
                            />
                        </div>
                    )}
                </div>
            </div>
            {description && (
                <div className={style.description}>
                    <span>{description}</span>
                </div>
            )}
            {isLiveGame && (
                <div className={style.liveInfo}>
                    {seatsInfo && (
                        <div className={style.seatsInfo}>
                            <span className={style.seatsIcon} />
                            <span>{seatsInfo}</span>
                        </div>
                    )}
                    {limits && <div className={style.liveLimits}>{limits}</div>}
                </div>
            )}
            {gamesYouMightLike?.length > 0 && (
                <div className={style.games}>
                    <div className={style.gamesTitle}>{t('playtech.mobile.system.games.youMightLike')}</div>
                    <GamesViewMini
                        gameCodes={gamesYouMightLike}
                        className={style.slider}
                        classNames={{ game: style.game }}
                        showGameInfo={true}
                    />
                </div>
            )}
        </div>
    );
};

export const GameInfo = compose(
    withTranslations,
    withProps(({ data: { code, queryString } }) => {
        const isLoggedIn = useUserSelector(prop('isLoggedIn'));
        const { gameCode, demoModeSupport, ...game } = useGameConfig(code);

        if (!gameCode) {
            log('warn', `Game with code '${code}' can not be found`);
            return;
        }

        return {
            ...game,
            queryString,
            code: gameCode,
            showFavorite: isLoggedIn,
            gamesYouMightLike: [],
            demoModeSupport: !(demoModeSupport === 'unavailable' || demoModeSupport === 'guest' && isLoggedIn)
        };
    }),
    withProps(withGameDescription(145))
)(GameInfoBare);
